import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack } from "@mui/material";
import { PlaylistAddCheck } from "@mui/icons-material";

import { useGetOrders } from "../../hooks/orders/useGetOrders";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import ExportButton from "../exports/ExportButton";
import { OrdersTableColumns } from "./Table/OrdersTableColumns";
import OrdersTableRow from "./Table/OrdersTableRow";
import { ORDER_STATUS_OPTIONS } from "./Table/OrderStatusBadge";
import BulkStatusModal from "./BulkStatusModal";
import CreateOrderModal from "./Create/CreateOrderModal";
import InvoiceViewerDialog from "./Detail/InvoiceViewerDialog";

const PAYMENT_METHOD_OPTIONS = [
    { value: "",       label: { ar: "كل الطرق",            en: "All Methods" } },
    { value: "cod",    label: { ar: "الدفع عند الاستلام", en: "COD" } },
    { value: "paymob", label: { ar: "بايموب",              en: "Paymob" } },
    { value: "manual", label: { ar: "يدوي",                en: "Manual" } },
];

const OrdersTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const navigate = useNavigate();

    const [statusFilter, setStatusFilter] = useState("");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
    const [hasCouponFilter, setHasCouponFilter] = useState("");
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [previewOrder, setPreviewOrder] = useState(null);

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSelectedIds,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleView,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/orders",
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        status: statusFilter || undefined,
        payment_method: paymentMethodFilter || undefined,
        has_coupon: hasCouponFilter === "" ? undefined : hasCouponFilter,
        sort_by: "placed_at",
        sort_order: order,
    }), [page, perPage, search, order, statusFilter, paymentMethodFilter, hasCouponFilter]);

    const { orders, meta, isLoading, error, refetch } = useGetOrders(params);

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                ...ORDER_STATUS_OPTIONS,
            ],
        },
        {
            name: "payment_method",
            label: { ar: "طريقة الدفع", en: "Payment Method" },
            value: paymentMethodFilter,
            onChange: setPaymentMethodFilter,
            options: PAYMENT_METHOD_OPTIONS,
        },
        {
            name: "has_coupon",
            label: { ar: "كوبون", en: "Has Coupon" },
            value: hasCouponFilter,
            onChange: setHasCouponFilter,
            options: [
                { value: "",  label: { ar: "الكل",       en: "All" } },
                { value: "1", label: { ar: "بكوبون",     en: "With coupon" } },
                { value: "0", label: { ar: "بدون كوبون", en: "Without coupon" } },
            ],
        },
    ];

    const renderRow = (rowParams) => (
        <OrdersTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onPreviewInvoice={(item) => setPreviewOrder(item)}
        />
    );

    return (
        <>
            {/* Sticky bulk-action bar — appears when at least one row is selected */}
            {selectedIds.length > 0 && (
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        bgcolor: (t) => t.palette.primary.main + "10",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box>
                        <strong>{selectedIds.length}</strong>{" "}
                        {isArabic ? "محدد" : "selected"}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<PlaylistAddCheck />}
                            onClick={() => setBulkModalOpen(true)}
                        >
                            {isArabic ? "تحديث الحالة" : "Update Status"}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedIds([])}
                        >
                            {isArabic ? "إلغاء التحديد" : "Clear"}
                        </Button>
                    </Stack>
                </Box>
            )}

            <TableLayout
                title={isArabic ? "الطلبات" : "Orders"}
                subtitle={isArabic ? "إدارة كل طلبات المتجر" : "Manage all storefront orders"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "طلب جديد" : "New Order"}
                onAddClick={() => setCreateModalOpen(true)}
                headerExtra={<ExportButton type="orders" filters={params} variant="contained" size="medium" />}
                isLoading={isLoading}
                error={error}
                items={orders}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={OrdersTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuView={handleView}
                menuDisableEdit
                menuDisableDelete
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, orders)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                filters={tableFilters}
                onClearAllFilters={() => {
                    setStatusFilter("");
                    setPaymentMethodFilter("");
                    setHasCouponFilter("");
                }}
                emptyLabelAr="لا توجد طلبات"
                emptyLabelEn="No orders found"
                refetch={refetch}
            />

            <BulkStatusModal
                open={bulkModalOpen}
                ids={selectedIds}
                onClose={() => setBulkModalOpen(false)}
                onSuccess={() => setSelectedIds([])}
            />

            <CreateOrderModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onCreated={(orderId) => {
                    setCreateModalOpen(false);
                    if (orderId) navigate(`/orders/${orderId}`);
                }}
            />

            <InvoiceViewerDialog
                open={Boolean(previewOrder)}
                order={previewOrder}
                onClose={() => setPreviewOrder(null)}
            />
        </>
    );
};

export default OrdersTable;
